import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Sparkles, Menu, X } from "lucide-react";

const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-[var(--lk-primary)]" : "text-[var(--lk-muted)] hover:text-[var(--lk-text)]"}`;

const NAV_LINKS = [
    { to: "/workspace", label: "Workspace", testId: "nav-workspace" },
    { to: "/blog", label: "Blog", testId: "nav-blog" },
    { to: "/calendar", label: "Calendar", testId: "nav-calendar" },
    { to: "/history", label: "History", testId: "nav-history" },
    { to: "/dashboard", label: "Dashboard", testId: "nav-dashboard" },
    { to: "/profile", label: "Profile", testId: "nav-profile" },
];
const ADMIN_LINKS = [{ to: "/admin", label: "Admin", testId: "nav-admin" }];

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
        setMobileOpen(false);
    };

    return (
        <header className="sticky top-0 z-40 backdrop-blur-md bg-[var(--lk-bg)]/80 border-b border-[var(--lk-border)]">
            <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
                <Link to={user ? "/workspace" : "/"} className="flex items-center gap-3 group" data-testid="nav-brand">
                    <div className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-[var(--lk-primary)]/60 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4 text-[var(--lk-primary)]" />
                        <span className="absolute inset-0 rounded-full lk-pulse border border-[var(--lk-primary)]/30" />
                    </div>
                    <div>
                        <div className="font-semibold text-lg sm:text-xl leading-none tracking-tight">Lakshmi</div>
                        <div className="lk-label mt-0.5 hidden sm:block">Your personal brand, automated</div>
                    </div>
                </Link>

                {user && (
                    <nav className="hidden md:flex items-center gap-6">
                        {NAV_LINKS.map(l => (
                            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.testId}>{l.label}</NavLink>
                        ))}
                        {user.role === "admin" && ADMIN_LINKS.map(l => (
                            <NavLink key={l.to} to={l.to} className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors font-semibold ${isActive ? "text-[var(--lk-primary)]" : "text-[var(--lk-primary)]/70 hover:text-[var(--lk-primary)]"}`
                            } data-testid={l.testId}>{l.label}</NavLink>
                        ))}
                    </nav>
                )}

                <div className="flex items-center gap-2 sm:gap-3">
                    {user ? (
                        <>
                            <div className="hidden sm:block text-right">
                                <div className="text-xs text-[var(--lk-muted)]">Signed in as</div>
                                <div className="text-sm truncate max-w-[140px]" data-testid="nav-user-email">{user.email}</div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="hidden md:flex lk-border rounded-full p-2 text-[var(--lk-muted)] hover:text-[var(--lk-text)]"
                                data-testid="nav-logout-btn"
                                title="Sign out">
                                <LogOut className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setMobileOpen(o => !o)}
                                className="md:hidden lk-border rounded-full p-2 text-[var(--lk-muted)]"
                                aria-label="Toggle menu">
                                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </>
                    ) : (
                        <>
                            <nav className="hidden md:flex items-center gap-5">
                                <Link to="/features" className="text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] transition-colors">Features</Link>
                                <Link to="/pricing" className="text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] transition-colors">Pricing</Link>
                                <Link to="/blog" className="text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] transition-colors">Blog</Link>
                                <Link to="/about" className="text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] transition-colors">About</Link>
                            </nav>
                            <Link to="/login" className="text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)]" data-testid="nav-login-link">Log in</Link>
                            <Link to="/signup" className="lk-btn-primary rounded-full px-4 py-2 text-sm" data-testid="nav-signup-btn">Get started</Link>
                        </>
                    )}
                </div>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && user && (
                <div className="md:hidden border-t border-[var(--lk-border)] bg-[var(--lk-bg)]/95 backdrop-blur-md px-4 py-4 flex flex-col gap-1">
                    {NAV_LINKS.map(l => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            className={({ isActive }) =>
                                `px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "text-[var(--lk-primary)] bg-[var(--lk-primary)]/10" : "text-[var(--lk-muted)] hover:text-[var(--lk-text)] hover:bg-[var(--lk-surface-2)]"}`
                            }
                            data-testid={`mobile-${l.testId}`}
                            onClick={() => setMobileOpen(false)}>
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="mt-3 pt-3 border-t border-[var(--lk-border)] flex items-center justify-between">
                        <div className="text-xs text-[var(--lk-muted)] truncate max-w-[180px]">{user.email}</div>
                        <button
                            onClick={handleLogout}
                            className="lk-border rounded-full px-3 py-2 text-xs text-[var(--lk-muted)] flex items-center gap-2"
                            data-testid="mobile-nav-logout-btn">
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
